import { useEffect, useState, type FormEvent, type KeyboardEvent } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Pencil } from 'lucide-react'
import { ApiError, BUTTON_CLASSES, BUTTON_SIZE_SM } from '@region13/ui'
import { inleagueWillRefuse, normalizeTeamName, teamLabel } from '@region13/shared-types'
import { api, type CoachTeamWithGames } from '../lib/api'

const REFUSED = "InLeague won't accept this name. Remove emoji and symbols and try again."

const inputClass =
  'min-h-[44px] px-3 rounded-lg border border-line bg-surface text-base text-ink ' +
  'focus:outline-none focus:ring-2 focus:ring-brand-maroon/30 focus:border-brand-maroon'

/** The team heading on Home. A head coach gets a pencil that turns it into an
 *  inline editor; the save goes to InLeague first (spec 2026-10-09), and the
 *  label everywhere updates from the refetched team list. */
export function TeamNameHeading({ team }: { team: CoachTeamWithGames }) {
  const qc = useQueryClient()
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState('')
  // The name on screen when the pencil was clicked; the server refuses the
  // save if InLeague no longer holds it, even if the list refetches mid-edit.
  const [expected, setExpected] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const refreshTeams = () => qc.invalidateQueries({ queryKey: ['teams'] })
  // Saved in InLeague, waiting on the sync: good news, not an error, and over
  // once the new name arrives (#703).
  const [pending, setPending] = useState<string | null>(null)
  useEffect(() => { setPending(null) }, [team.name])

  const save = useMutation({
    mutationFn: (name: string) => api.renameTeam(team.externalId, name, expected),
    onSuccess: () => {
      setEditing(false)
      setError(null)
      void refreshTeams()
    },
    onError: (e) => {
      setError(e instanceof ApiError ? e.message : "Couldn't save the team name. Try again.")
      // The pinned name is stale after either, so a Save from the open editor
      // would be refused as `changed` again. Close it, keep the message, and let
      // the pencil pin the refreshed name (#703).
      if (e instanceof ApiError && (e.code === 'changed' || e.code === 'saved_pending')) {
        setEditing(false)
        void refreshTeams()
      }
      if (e instanceof ApiError && e.code === 'saved_pending') { setError(null); setPending(e.message) }
    },
  })

  const errorLine = error
    ? <p role="alert" className="text-sm text-brand-red-dark">{error}</p>
    : pending && <p role="status" className="text-sm text-ink-soft">{pending}</p>
  const label = teamLabel({ designation: team.externalId, coachName: team.coachName, teamName: team.name })

  if (!editing) {
    return (
      <div>
        <div className="flex items-center gap-2">
          <h2 className="font-semibold text-ink">{label}</h2>
          {team.isHeadCoach === 1 && (
            <button
              type="button"
              aria-label="Edit team name"
              className="inline-flex h-11 w-11 items-center justify-center rounded text-ink-soft hover:text-brand-maroon"
              onClick={() => { setDraft(team.name ?? ''); setExpected(team.name); setError(null); setPending(null); setEditing(true) }}
            >
              <Pencil className="h-4 w-4" aria-hidden="true" />
            </button>
          )}
        </div>
        {errorLine}
      </div>
    )
  }

  const submit = (e?: FormEvent) => {
    e?.preventDefault()
    const name = normalizeTeamName(draft)
    if (name !== null && inleagueWillRefuse(name)) { setError(REFUSED); return }
    save.mutate(name ?? '')
  }
  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') { e.preventDefault(); setEditing(false); setError(null) }
  }

  return (
    <form onSubmit={submit} className="flex w-full flex-col gap-1">
      <div className="flex flex-wrap items-center gap-2">
        <input
          aria-label="Team name"
          // Fills the row so a long name is readable; on a phone the buttons
          // wrap under it rather than squeezing it.
          className={`${inputClass} min-w-0 flex-1 basis-full sm:basis-0`}
          value={draft}
          autoFocus
          disabled={save.isPending}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKeyDown}
        />
        <button type="submit" className={`${BUTTON_CLASSES.primary} ${BUTTON_SIZE_SM}`} disabled={save.isPending}>
          {save.isPending ? 'Saving…' : 'Save'}
        </button>
        <button
          type="button"
          className={`${BUTTON_CLASSES.secondary} ${BUTTON_SIZE_SM}`}
          disabled={save.isPending}
          onClick={() => { setEditing(false); setError(null) }}
        >
          Cancel
        </button>
      </div>
      {errorLine}
    </form>
  )
}
